import { useMemo, useState } from "react";
import { seo } from "@/lib/seo";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { ArrowLeft, BarChart3, Sparkles } from "lucide-react";
import { fetchTests } from "@/lib/tests.functions";
import { formatPrettyDate, localDateString } from "@/lib/app-types";
import { type TestPlan } from "@/lib/test-plan";
import { buildInsights, computePerformance, type ScoredTest } from "@/lib/test-performance";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/tests/performance")({
  head: () => ({
    ...seo({
      title: "Test Performance — The Quantum Minds",
      description:
        "Average score, subject-wise strengths, improvement trends, AI insights and your full test history.",
      noindex: true,
    }),
  }),
  component: PerformancePage,
});

type Filter = "recent" | "highest" | "lowest" | "month";

function PerformancePage() {
  const today = localDateString();
  const [filter, setFilter] = useState<Filter>("recent");
  const [subject, setSubject] = useState<"all" | "physics" | "chemistry" | "maths">("all");

  const { data, isPending } = useQuery({ queryKey: ["tests"], queryFn: () => fetchTests() });
  const tests = useMemo<TestPlan[]>(() => data ?? [], [data]);
  const perf = useMemo(() => computePerformance(tests, today), [tests, today]);
  const insights = useMemo(() => buildInsights(tests, today), [tests, today]);

  const history = useMemo(() => {
    let list = [...perf.history];
    if (subject !== "all") list = list.filter((t) => t[subject] !== null);
    if (filter === "highest") list.sort((a, b) => b.percent - a.percent);
    if (filter === "lowest") list.sort((a, b) => a.percent - b.percent);
    if (filter === "month") {
      const cutoff = new Date(`${today}T00:00:00Z`).getTime() - 30 * 86400000;
      list = list.filter((t) => new Date(`${t.testDate}T00:00:00Z`).getTime() >= cutoff);
    }
    return list;
  }, [perf.history, filter, subject, today]);

  return (
    <main className="page-shell px-5 lg:px-8 pt-8">
      <div className="flex items-center gap-3">
        <Link
          to="/tests"
          aria-label="Back to Test Tracker"
          className="flex size-9 shrink-0 items-center justify-center rounded-full border border-hairline bg-surface"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="text-[22px] font-bold tracking-tight">Performance</h1>
          <p className="text-[12.5px] text-muted-foreground">Across every scored test</p>
        </div>
        <span className="neural-gradient neural-halo relative isolate z-0 flex size-10 shrink-0 items-center justify-center rounded-2xl">
          <BarChart3 className="size-5 text-white" />
        </span>
      </div>

      {isPending ? (
        <div className="glass mt-6 h-48 animate-pulse rounded-[26px]" />
      ) : perf.attempted === 0 ? (
        <div className="glass mt-8 rounded-[26px] px-6 py-12 text-center">
          <h2 className="text-[17px] font-bold tracking-tight">No scores yet</h2>
          <p className="mt-1.5 text-[13px] text-muted-foreground">
            Enter a score on a test day and your analytics start building here.
          </p>
        </div>
      ) : (
        <>
          <section className="mt-6 grid grid-cols-2 gap-3">
            <Stat label="Tests attempted" value={String(perf.attempted)} />
            <Stat label="Tests missed" value={String(perf.missed)} />
            <Stat label="Average score" value={String(perf.averageScore)} />
            <Stat label="Average %" value={`${perf.averagePercent}%`} />
            <Stat
              label="Highest score"
              value={perf.highest ? `${perf.highest.obtained}/${perf.highest.total}` : "—"}
            />
            <Stat
              label="Lowest score"
              value={perf.lowest ? `${perf.lowest.obtained}/${perf.lowest.total}` : "—"}
            />
            <Stat label="Overall improvement" value={signed(perf.improvement, "%")} />
            <Stat label="Improving streak" value={`${perf.streak} tests`} />
            <Stat label="Weekly trend" value={signed(perf.weeklyTrend, "%")} />
            <Stat label="Monthly trend" value={signed(perf.monthlyTrend, "%")} />
          </section>

          {perf.best ? (
            <section className="glass mt-4 rounded-[26px] p-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-primary">
                Best ever performance
              </p>
              <p className="mt-2 text-[17px] font-bold tracking-tight">{perf.best.name}</p>
              <p className="mt-1 text-[12.5px] text-muted-foreground">
                {perf.best.obtained}/{perf.best.total} · {perf.best.percent}% ·{" "}
                {formatPrettyDate(perf.best.testDate)}
              </p>
            </section>
          ) : null}

          {perf.subjects.length ? (
            <section className="glass mt-4 rounded-[26px] p-5">
              <h2 className="text-[15px] font-bold tracking-tight">Subject-wise performance</h2>
              <div className="mt-3 space-y-3">
                {perf.subjects.map((s) => (
                  <div key={s.key}>
                    <div className="flex items-center justify-between text-[12px] font-semibold">
                      <span>{s.label}</span>
                      <span className="text-muted-foreground">
                        avg {s.average} · {s.percent}%
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-surface">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(100, s.percent)}%` }}
                        transition={{ type: "spring", stiffness: 120, damping: 24 }}
                        className="neural-gradient h-full rounded-full"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[12px] text-muted-foreground">
                Strongest:{" "}
                <span className="font-semibold text-success">{perf.strongest?.label ?? "—"}</span> ·
                Weakest:{" "}
                <span className="font-semibold text-warning">{perf.weakest?.label ?? "—"}</span>
              </p>
            </section>
          ) : null}

          <ScoreGraph list={[...perf.history].reverse()} />

          {insights.length ? (
            <section className="glass mt-4 rounded-[26px] p-5">
              <div className="flex items-center gap-2">
                <Sparkles className="size-4 text-primary" />
                <h2 className="text-[15px] font-bold tracking-tight">AI insights</h2>
              </div>
              <div className="mt-3 space-y-2.5">
                {insights.map((i) => (
                  <div
                    key={i.label}
                    className="rounded-[18px] border border-hairline bg-surface p-3.5"
                  >
                    <p className="text-[10.5px] font-bold uppercase tracking-wider text-primary">
                      {i.label}
                    </p>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
                      {i.body}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section className="mt-6">
            <h2 className="text-[15px] font-bold tracking-tight">Test history</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {(
                [
                  ["recent", "Recently attempted"],
                  ["highest", "Highest score"],
                  ["lowest", "Lowest score"],
                  ["month", "This month"],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setFilter(id)}
                  className={cn(
                    "rounded-full border px-3.5 py-1.5 text-[11.5px] font-semibold transition-colors",
                    filter === id
                      ? "border-primary/40 bg-primary/12 text-primary"
                      : "border-hairline bg-surface text-muted-foreground",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(
                [
                  ["all", "All subjects"],
                  ["physics", "Physics"],
                  ["chemistry", "Chemistry"],
                  ["maths", "Mathematics"],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSubject(id)}
                  className={cn(
                    "rounded-full border px-3.5 py-1.5 text-[11.5px] font-semibold transition-colors",
                    subject === id
                      ? "border-primary/40 bg-primary/12 text-primary"
                      : "border-hairline bg-surface text-muted-foreground",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="mt-4 space-y-2.5 pb-4">
              {history.length === 0 ? (
                <p className="rounded-[20px] border border-hairline bg-surface px-4 py-3 text-[12.5px] text-muted-foreground">
                  No tests match this filter.
                </p>
              ) : null}
              {history.map((t) => (
                <Link
                  key={t.id}
                  to="/tests/$testId"
                  params={{ testId: t.id }}
                  className="glass block rounded-[22px] p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-[14px] font-bold tracking-tight">{t.name}</p>
                      <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                        {formatPrettyDate(t.testDate)}
                      </p>
                    </div>
                    <p className="shrink-0 text-right text-[13px] font-bold">
                      <span className="neural-text">
                        {t.obtained}/{t.total}
                      </span>
                      <span className="block text-[11px] font-semibold text-muted-foreground">
                        {t.percent}%
                      </span>
                    </p>
                  </div>
                  <p className="mt-2 text-[11.5px] text-muted-foreground">
                    P {t.physics ?? "—"} · C {t.chemistry ?? "—"} · M {t.maths ?? "—"} · Completed
                  </p>
                </Link>
              ))}
            </div>
          </section>
        </>
      )}
    </main>
  );
}

function signed(value: number, suffix = ""): string {
  const v = Math.round(value * 10) / 10;
  return `${v > 0 ? "+" : ""}${v}${suffix}`;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="glass rounded-[22px] px-4 py-3.5">
      <p className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-[18px] font-extrabold tracking-tight">{value}</p>
    </div>
  );
}

/** Lightweight CSS bar chart of score percentages over time. */
function ScoreGraph({ list }: { list: ScoredTest[] }) {
  if (list.length < 1) return null;
  return (
    <section className="glass mt-4 rounded-[26px] p-5">
      <h2 className="text-[15px] font-bold tracking-tight">Score history</h2>
      <div className="mt-4 flex h-32 items-end gap-1.5">
        {list.map((t) => (
          <div key={t.id} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1.5">
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${Math.max(4, t.percent)}%` }}
              transition={{ type: "spring", stiffness: 120, damping: 22 }}
              className="neural-gradient w-full rounded-t-md"
              title={`${t.name}: ${t.percent}%`}
            />
            <span className="w-full truncate text-center text-[9px] text-muted-foreground">
              {t.percent}%
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
